import appConfig from '../config';
import { useState, useEffect } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import StakeTable, { Column } from "./GameEngine/StakeTable";
import { Button, Modal, FormInput, FormSelect, FormMultiSelect, PageHeader, ConfirmDialog } from "../components/ui";
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';

interface Collaborator {
  id: number;
  name: string;
  username?: string;
  email: string;
  password2: string;
  balance: string;
  created: string;
  parent_id: number;
  referalcode: string;
  referral_link: string;
  phone: string;
  first_name: string;
  last_name: string;
  parent_name?: string;
  is_active: boolean;
  is_blocked: boolean;
  profile?: any;
}

const CollaboratorUser = () => {
  const currentRole = (localStorage.getItem('role') || '').toLowerCase();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const managerParam = searchParams.get('managerFilter');

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    username: "",
    email: "",
    password: "",
    role: "affiliate",
    amount: "0",
    manager_id: ""
  });

  const queryClient = useQueryClient();
  const baseURL = appConfig.baseURL;

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [selectedProfileDetails, setSelectedProfileDetails] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [managerFilter, setManagerFilter] = useState<string[]>(managerParam ? [managerParam] : []);
  const [affiliateFilter, setAffiliateFilter] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (managerParam) {
      setManagerFilter([managerParam]);
    }
  }, [managerParam]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("error");

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    id: number | null;
    username: string;
    isActive: boolean;
  }>({ open: false, id: null, username: '', isActive: false });

  const [approveStatus, setApproveStatus] = useState<Record<number, { loading: boolean; message: string; type: "success" | "error" }>>({});

  const { data: managers = [] } = useQuery({
    queryKey: ['managerListDropdown'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${baseURL}/collaborator/managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data?.data || [];
    }
  });

  const { data: myProfile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${baseURL}/admin/getMineProfile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data?.data;
    }
  });


  const { data: adminsData = { data: [], total: 0 }, isLoading: isLoadingAdmins } = useQuery({
    queryKey: ['affiliates', page, limit, searchTerm, managerFilter],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const offset = (page - 1) * limit;
      let url = `${baseURL}/collaborator/list?role=affiliate&limit=${limit}&offset=${offset}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      if (managerFilter.length > 0) {
        url += `&managerName=${managerFilter.join(',')}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return {
        data: res.data?.data || [],
        total: res.data?.total || 0
      };
    }
  });

  const admins = adminsData.data;
  const filteredAdmins = admins.filter((a: any) => {
    if (currentRole !== 'manager' || affiliateFilter.length === 0) return true;
    return affiliateFilter.includes(a.username);
  });
  const total = adminsData.total;
  const totalPages = Math.ceil(total / limit);

  const fetchProfileDetails = async (id: number) => {
    try {
      setLoadingProfile(true);
      setProfileModalVisible(true);
      setSelectedProfileDetails(null);

      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${baseURL}/collaborator/getProfileDetailsCollaboratorChild/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSelectedProfileDetails(response.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleApproveAffiliate = async (affiliateId: number) => {
    setApproveStatus(prev => ({
      ...prev,
      [affiliateId]: { loading: true, message: "", type: "success" }
    }));

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${baseURL}/collaborator/approve/${affiliateId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: { loading: false, message: response.data.message || "Approved!", type: "success" }
      }));

      // Refresh list
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });

    } catch (error: any) {
      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: {
          loading: false,
          message: error?.response?.data?.message || "Failed to approve.",
          type: "error"
        }
      }));
    }
  };

  const handleRejectAffiliate = async (affiliateId: number) => {
    if (!window.confirm("Are you sure you want to reject and delete this affiliate applicant?")) {
      return;
    }
    setApproveStatus(prev => ({
      ...prev,
      [affiliateId]: { loading: true, message: "", type: "success" }
    }));

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${baseURL}/collaborator/reject/${affiliateId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: { loading: false, message: response.data.message || "Rejected!", type: "success" }
      }));

      // Refresh list
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });

    } catch (error: any) {
      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: {
          loading: false,
          message: error?.response?.data?.message || "Failed to reject.",
          type: "error"
        }
      }));
    }
  };

  const assignManagerMutation = useMutation({
    mutationFn: async ({ affiliateId, managerId }: { affiliateId: number; managerId: string }) => {
      const token = localStorage.getItem("token");
      return axios.put(
        `${baseURL}/collaborator/assignManager/${affiliateId}`,
        { manager_id: managerId || null },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
    },
    onSuccess: (res) => {
      setMessage(res.data?.message || "Manager updated successfully!");
      setMessageType("success");
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.message || "Failed to assign manager.");
      setMessageType("error");
      setTimeout(() => setMessage(""), 3000);
    }
  });

  const handleAssignManager = (affiliateId: number, managerId: string) => {
    assignManagerMutation.mutate({ affiliateId, managerId });
  };

  const toggleStatusMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem("token");
      return apiClient.put(`/collaborator/toggle-status/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType("success");
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
      setTimeout(() => setMessage(""), 3000);
    },
    onError: (err: any) => {
      setMessage(err.response?.data?.message || "Failed to toggle status");
      setMessageType("error");
      setTimeout(() => setMessage(""), 3000);
    }
  });

  const handleToggleStatus = (id: number, username: string, isActive: boolean) => {
    setConfirmDialog({ open: true, id, username, isActive });
  };

  const handleConfirmToggle = () => {
    if (confirmDialog.id !== null) {
      toggleStatusMutation.mutate(confirmDialog.id);
    }
    setConfirmDialog({ open: false, id: null, username: '', isActive: false });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {

    setFormData({
      ...formData,
      [e.target.name]:
        e.target.value
    });
  };

  const createMutation = useMutation({
    mutationFn: async (newAffiliate: any) => {
      const token = localStorage.getItem("token");
      return axios.post(`${baseURL}/collaborator/affiliate/create`, newAffiliate, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType("success");
      queryClient.invalidateQueries({ queryKey: ['affiliates'] });
      setTimeout(() => setShowModal(false), 1500);
    },
    onError: (error: any) => {
      setMessage(error?.response?.data?.message || "Failed");
      setMessageType("error");
    },
    onSettled: () => setLoading(false)
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    createMutation.mutate(formData);
  };

  const columns: Column[] = [
    { key: 'sn', label: 'S.No.', render: (_, __, i) => (page - 1) * limit + i + 1 },
    { key: 'username', label: 'Username', render: (_, item: Collaborator) => item.username || "-" },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { 
      key: 'parent_id', 
      label: 'Manager', 
      render: (_, item: Collaborator) => {
        if (currentRole === 'admin') {
          return (
            <select
              value={item.parent_id || ''}
              onChange={(e) => handleAssignManager(item.id, e.target.value)}
              className="bg-[#1e2337] border border-slate-700 rounded px-2.5 py-1 text-xs text-white max-w-[170px] focus:outline-none focus:border-indigo-500 cursor-pointer"
            >
              <option value="">No Manager</option>
              {(Array.isArray(managers) ? managers : []).map((m: any) => (
                <option key={m.id} value={m.id}>
                  {m.username}
                </option>
              ))}
            </select>
          );
        }
        return item.parent_name ? `${item.parent_name} (${item.parent_id})` : '-';
      }
    },
    { key: 'referalcode', label: 'Referral Code' },
    { key: 'referral_link', label: 'Refer Link' },
    { 
      key: 'user_count', 
      label: 'Users', 
      render: (val, item: any) => (
        <span 
          onClick={() => navigate(`/users?collabFilter=${encodeURIComponent(item.username || item.name)}`)}
          className="text-indigo-400 hover:text-indigo-300 font-bold cursor-pointer underline decoration-dotted"
        >
          {val || 0}
        </span>
      )
    },
    { key: 'ip', label: 'IP Address', render: (_, item: Collaborator) => item.profile?.ip || "-" },
    { key: 'geo', label: 'Geo Location', render: (_, item: Collaborator) => {
        const geo = item.profile?.geo;
        if (!geo || (!geo.city && !geo.country)) return "-";
        return [geo.city, geo.state || geo.region, geo.country].filter(Boolean).join(", ");
      }
    },
    { key: 'created', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
    {
      key: 'status',
      label: 'Status',
      render: (_, item: Collaborator) => {
        if (item.is_active) {
          return <span className="px-2.5 py-1 rounded text-xs font-bold whitespace-nowrap bg-emerald-950 text-emerald-400 border border-emerald-500/20">Active</span>;
        }
        if (item.is_blocked) {
          return <span className="px-2.5 py-1 rounded text-xs font-bold whitespace-nowrap bg-red-950 text-red-400 border border-red-500/20">Blocked</span>;
        }
        return <span className="px-2.5 py-1 rounded text-xs font-bold whitespace-nowrap bg-amber-950 text-amber-400 border border-amber-500/20">Pending</span>;
      }
    },
    { key: 'actions', label: 'Action', render: (_, item: Collaborator) => {
      // Pending = not yet approved (is_active=false AND is_blocked=false)
      // Blocked = was active, then blocked (is_active=false AND is_blocked=true)
      const isPending = !item.is_active && !item.is_blocked;

      return (
        <div className="flex flex-col gap-1.5 min-w-[180px]">
          <div className="flex gap-2 flex-wrap">
            {/* Pending affiliate — show Approve / Reject only */}
            {isPending ? (
              <>
                <button
                  onClick={() => handleApproveAffiliate(item.id)}
                  disabled={approveStatus[item.id]?.loading}
                  className="bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded text-xs text-white font-bold disabled:opacity-50 cursor-pointer transition-colors"
                >
                  Approve
                </button>
                <button
                  onClick={() => handleRejectAffiliate(item.id)}
                  disabled={approveStatus[item.id]?.loading}
                  className="bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded text-xs text-white font-bold disabled:opacity-50 cursor-pointer transition-colors"
                >
                  Reject
                </button>
              </>
            ) : (
              /* Active or Blocked affiliate — show Block / Unblock only */
              item.is_active ? (
                <button
                  onClick={() => handleToggleStatus(item.id, item.username || item.name, item.is_active)}
                  disabled={toggleStatusMutation.isPending}
                  className="bg-amber-600 hover:bg-amber-700 px-2.5 py-1.5 rounded text-xs text-white font-bold disabled:opacity-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Block
                </button>
              ) : (
                <button
                  onClick={() => handleToggleStatus(item.id, item.username || item.name, item.is_active)}
                  disabled={toggleStatusMutation.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1.5 rounded text-xs text-white font-bold disabled:opacity-50 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Unblock
                </button>
              )
            )}
          </div>
          {approveStatus[item.id]?.message && (
            <p className={`text-[10px] ${approveStatus[item.id]?.type === "success" ? "text-emerald-400" : "text-red-400"}`}>
              {approveStatus[item.id].message}
            </p>
          )}
        </div>
      );
    } }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-4 sm:p-6 text-white min-h-screen bg-[#151929]">

      <PageHeader 
        title="Affiliate Management" 
      />

      {message && (
        <div className={`mb-4 text-center py-2 px-4 rounded-lg shadow-md text-sm ${messageType === 'success' ? 'bg-green-600/20 text-green-400 border border-green-500/20' : 'bg-red-600/20 text-red-400 border border-red-500/20'}`}>
          {message}
        </div>
      )}

      <div className="mb-6">
        <StakeTable
          title=""
          columns={columns}
          data={filteredAdmins}
          searchPlaceholder="Search by name or email..."
          actions={
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowFilters(f => !f)}
                className="flex lg:hidden items-center gap-1 px-2 py-1.5 bg-[#1e2337] border border-slate-700 rounded-lg text-xs font-semibold text-slate-300 hover:text-white hover:border-indigo-500 transition-all h-9"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                {showFilters ? 'Hide' : 'Filters'}
                {(currentRole === 'manager' ? affiliateFilter.length : managerFilter.length) > 0 && (
                  <span className="ml-1 bg-indigo-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                    {currentRole === 'manager' ? affiliateFilter.length : managerFilter.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-2.5 rounded-lg flex items-center gap-1 transition-colors shadow-sm whitespace-nowrap text-xs h-9"
              >
                <span>+</span>
                <span>Create Affiliate</span>
              </button>
            </div>
          }
          bottomActions={
            currentRole === 'manager' ? (
              <div className={`w-full sm:w-[200px] ${showFilters ? 'block' : 'hidden lg:block'}`}>
                <FormMultiSelect
                  selectedValues={affiliateFilter}
                  onChange={(vals) => { setAffiliateFilter(vals); setPage(1); }}
                  placeholder="All Affiliates"
                  options={admins.map((a: any) => ({ value: a.username, label: a.username })).filter((opt: any) => opt.value)}
                />
              </div>
            ) : (
              <div className={`w-full sm:w-[200px] ${showFilters ? 'block' : 'hidden lg:block'}`}>
                <FormMultiSelect
                  selectedValues={managerFilter}
                  onChange={(vals) => { setManagerFilter(vals); setPage(1); }}
                  placeholder="All Managers"
                  options={[
                    ...managers.map((m: any) => ({ value: m.username, label: m.username }))
                  ]}
                />
              </div>
            )
          }
          onSearch={(val) => {
            setSearchTerm(val);
            setPage(1);
          }}
          emptyMessage={isLoadingAdmins ? "Loading affiliates..." : "No affiliates found"}
          hasSidebarMargin={false}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </div>



      {/* create modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Create Affiliate"
        width="max-w-md"
      >
        <p className="text-sm text-indigo-300 mb-4 bg-indigo-500/10 p-3 rounded-lg border border-indigo-500/20">
          Creating under: <span className="font-bold">{myProfile?.username}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Username" name="username" value={formData.username} onChange={handleChange} required />
          <div className="grid grid-cols-2 gap-4">
            <FormInput label="First Name" name="first_name" value={formData.first_name} onChange={handleChange} required />
            <FormInput label="Last Name" name="last_name" value={formData.last_name} onChange={handleChange} required />
          </div>
          <FormInput label="Phone Number" name="phone_number" value={formData.phone_number} onChange={handleChange} required />
          <FormInput label="Email" type="email" name="email" value={formData.email} onChange={handleChange} required />
          <FormInput label="Password" type="password" name="password" value={formData.password} onChange={handleChange} required />
          
          {currentRole !== 'manager' && (
            <FormSelect 
              label="Manager (Optional)"
              name="manager_id"
              value={formData.manager_id}
              onChange={handleChange}
              options={[
                { value: "", label: "Select Manager (Optional)" },
                ...(Array.isArray(managers) ? managers : []).map((m: any) => ({ value: m.id, label: `${m.name} (${m.email})` }))
              ]}
            />
          )}

          <div className="pt-2 flex justify-end gap-3 border-t border-slate-800">
            <Button variant="ghost" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" loading={loading}>Create Affiliate</Button>
          </div>

          {message && (
            <div className={`mt-2 p-3 rounded text-sm text-center ${messageType === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
              {message}
            </div>
          )}
        </form>
      </Modal>

      {/* profile modal */}
      <Modal
        open={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
        title="Collaborator Statistics"
        width="max-w-2xl"
        footer={
          <Button variant="ghost" onClick={() => setProfileModalVisible(false)}>
            Close
          </Button>
        }
      >
        {loadingProfile ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-t-indigo-500 border-r-indigo-500/30 border-b-indigo-500/10 border-l-indigo-500/30 animate-spin" />
          </div>
        ) : selectedProfileDetails ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-white">
            {[
              { label: "Total Users", val: selectedProfileDetails.totalUser || 0 },
              { label: "Invited Collaborators", val: selectedProfileDetails.invitedChild || 0 },
              { label: "Total Deposit", val: `₹${selectedProfileDetails.totalDeposit || 0}`, color: "text-emerald-400" },
              { label: "Deposit Count", val: selectedProfileDetails.numberOfDepositCount || 0, color: "text-emerald-400" },
              { label: "First Deposit Amount", val: `₹${selectedProfileDetails.firstDeposit || 0}`, color: "text-emerald-400", colSpan: 2 },
              { label: "Total Withdraw", val: `₹${selectedProfileDetails.totalWithdraw || 0}`, color: "text-rose-400" },
              { label: "Withdraw Count", val: selectedProfileDetails.numberOfWithdrawCount || 0, color: "text-rose-400" },
              { label: "First Withdraw Amount", val: `₹${selectedProfileDetails.firstWithdraw || 0}`, color: "text-rose-400", colSpan: 2 },
            ].map(stat => (
              <div key={stat.label} className={`bg-slate-900 border border-slate-800 p-4 rounded-xl ${stat.colSpan === 2 ? 'sm:col-span-2' : ''}`}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">{stat.label}</p>
                <p className={`text-2xl font-black ${stat.color || 'text-white'}`}>{stat.val}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg text-center">
            Failed to load details.
          </div>
        )}
      </Modal>

      {/* Block / Unblock Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null, username: '', isActive: false })}
        onConfirm={handleConfirmToggle}
        title={confirmDialog.isActive ? '🔒 Block Affiliate' : '🔓 Unblock Affiliate'}
        message={
          confirmDialog.isActive
            ? `Are you sure you want to block "${confirmDialog.username}"? They will not be able to login and their referral link will stop working.`
            : `Are you sure you want to unblock "${confirmDialog.username}"? They will regain access to login and their referral link will work again.`
        }
        confirmLabel={confirmDialog.isActive ? 'Yes, Block' : 'Yes, Unblock'}
        confirmVariant={confirmDialog.isActive ? 'warning' : 'success'}
        loading={toggleStatusMutation.isPending}
      />

    </div>
  );
};

export default CollaboratorUser;